import { useEffect, useState } from "react";
import { Sparkles, Loader2, Volume2, Square, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";

const URG = { bassa: "bg-emerald-100 text-emerald-800", media: "bg-amber-100 text-amber-800", alta: "bg-red-100 text-red-800" };

export function AiAssistant({ onApply }) {
  const [models, setModels] = useState([]);
  const [provider, setProvider] = useState("openai");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { api.get("/ai/models").then((r) => setModels(r.data)).catch(() => {}); }, []);
  const ask = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/assist", { text, provider }); setRes(data.output); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-forest/20 bg-forest/5 p-5" data-testid="ai-assistant">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-semibold"><Sparkles size={18} className="text-terracotta" /> Assistente Ci penso io</div>
        <select data-testid="ai-provider-select" className="input-craft !w-auto !py-1.5 text-xs" value={provider} onChange={(e) => setProvider(e.target.value)}>{models.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}</select>
      </div>
      <p className="mt-1 text-xs text-ink/55">Racconta cosa succede in casa con parole tue: l'assistente individua il servizio giusto, l'urgenza e prepara il riepilogo per il back office.</p>
      <textarea data-testid="ai-assist-input" className="input-craft mt-3 min-h-[90px]" placeholder="Es. Da ieri sotto il lavello della cucina c'è acqua e il mobile è umido…" value={text} onChange={(e) => setText(e.target.value)} />
      <button data-testid="ai-assist-submit" className="btn-secondary mt-3 !py-2 text-sm" disabled={busy || text.trim().length < 10} onClick={ask}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Chiedi all'assistente</button>
      {res && (
        <div className="mt-4 rounded-xl bg-white border border-craft p-4 text-sm space-y-2" data-testid="ai-assist-result">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold">{res.service_name}</span><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${URG[res.urgency] || "bg-craft"}`} data-testid="ai-urgency">urgenza {res.urgency}</span><span className="text-[10px] text-ink/40 font-mono">{res.model}</span></div>
          <p className="text-ink/75">{res.reply}</p>
          <p className="text-xs text-ink/55"><span className="font-semibold">Riepilogo per il back office:</span> {res.summary}</p>
          {res.questions?.length > 0 && <ul className="text-xs text-ink/60 list-disc pl-4">{res.questions.map((q) => <li key={q}>{q}</li>)}</ul>}
          {onApply && <button data-testid="ai-assist-apply" className="btn-primary !py-1.5 !px-3 text-xs" onClick={() => onApply(res, text)}>Usa questo suggerimento</button>}
        </div>
      )}
    </div>
  );
}

export function ListenButton({ text, voice = "coral", className = "", label = "Ascolta" }) {
  const [state, setState] = useState("idle");
  const [audio, setAudio] = useState(null);
  const toggle = async () => {
    if (state === "playing") { audio?.pause(); setState("idle"); return; }
    setState("loading");
    try {
      const { data } = await api.post("/ai/tts", { text: text.slice(0, 4000), voice }, { responseType: "blob" });
      const a = new Audio(URL.createObjectURL(data));
      a.onended = () => setState("idle");
      setAudio(a); await a.play(); setState("playing");
    } catch (e) { toast.error("Audio non disponibile"); setState("idle"); }
  };
  const Icon = { loading: Loader2, playing: Square }[state] || Volume2;
  return (
    <button data-testid="listen-button" className={`btn-ghost text-xs ${className}`} onClick={toggle} disabled={state === "loading"}>
      <Icon size={14} className={state === "loading" ? "animate-spin" : ""} /> {state === "playing" ? "Ferma" : label}
    </button>
  );
}

export function AiImagePreview({ onGenerated }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [img, setImg] = useState(null);
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/image", { prompt }); setImg(data.id); onGenerated?.(data.id); toast.success("Anteprima generata e allegata"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-craft p-4" data-testid="ai-image">
      <div className="flex items-center gap-2 text-sm font-semibold"><ImagePlus size={16} className="text-terracotta" /> Anteprima dello stile desiderato (AI)</div>
      <p className="text-xs text-ink/55 mt-1">Descrivi il risultato che immagini: generiamo un'immagine indicativa da allegare alla richiesta, utile al back office per capire il gusto.</p>
      <div className="mt-2 flex gap-2"><input data-testid="ai-image-prompt" className="input-craft" placeholder="Es. salotto con carta da parati verde salvia a foglie, tende in lino" value={prompt} onChange={(e) => setPrompt(e.target.value)} /><button data-testid="ai-image-submit" className="btn-secondary !py-2 text-sm whitespace-nowrap" disabled={busy || prompt.trim().length < 5} onClick={gen}>{busy ? <Loader2 size={16} className="animate-spin" /> : "Genera"}</button></div>
      {img && <img src={fileUrl(img)} alt="Anteprima AI" className="mt-3 h-48 w-full rounded-xl object-cover border border-craft" data-testid="ai-image-result" />}
    </div>
  );
}
